// Per-format scoresheet PDF builders (jsPDF + jspdf-autotable v5 functional API).
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ScoresheetTeam, extractTeams, maxPlayers, teamCellLabel, drawHeaderBlock, drawNotesSection, PAGE_MARGIN,
} from './pdfCommon';
import { generateRoundRobinSchedule, RoundRobinSchedule } from './roundRobinSchedule';
import { bracketSeeds, defaultPlayoffTeams } from '../playoffSlots';
import { normalizeBracketSize } from '@staty/shared/utils/bracketTopology';

function lastY(doc: jsPDF): number {
  return (doc as any).lastAutoTable?.finalY ?? PAGE_MARGIN;
}

function newDoc(): jsPDF {
  return new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
}

// Serpentine (snake) pool split — mirrors distributeTeamsSerpentine in DivisionPage.
function splitIntoPools<T>(teams: T[], numPools: number): T[][] {
  const pools: T[][] = Array.from({ length: numPools }, () => []);
  for (let i = 0; i < teams.length; i += 1) {
    const roundIdx = Math.floor(i / numPools);
    const posInRound = i % numPools;
    const poolIdx = roundIdx % 2 === 0 ? posInRound : numPools - 1 - posInRound;
    pools[poolIdx].push(teams[i]);
  }
  return pools;
}

// ── shared sub-renderers ────────────────────────────────────────────────────

// The roster grid: Team # | Placing | Players | R1..Rn | Wins | Losses | Points.
// `seedOffset` re-numbers the "Team #" column for pool grids. `labels` lets a
// variant override the two points headers without changing the shared defaults.
function drawRosterGrid(
  doc: jsPDF,
  teams: ScoresheetTeam[],
  roundsCount: number,
  startY: number,
  labels?: { pointsScored?: string; points?: string },
): number {
  const pointsScoredLabel = labels?.pointsScored ?? 'Points Scored';
  const pointsLabel = labels?.points ?? 'Points';
  const maxP = maxPlayers(teams);
  const playerHead = maxP === 0 ? ['Team'] : maxP === 1 ? ['Player'] : ['Player 1', 'Player 2'];
  const rHead = Array.from({ length: roundsCount }, (_, i) => `R${i + 1}`);
  // Every real column label lives on ONE aligned header row; "Points Scored" sits
  // on a separate row above, spanning only the per-round R1..Rn columns (blank
  // cells elsewhere) so R1/R2/R3 line up with Team #, Wins, Losses, etc.
  const labelRow = ['Team #', 'Placing', ...playerHead, ...rHead, 'Wins', 'Losses', pointsLabel];
  const head: any = roundsCount > 0
    ? [
        [
          '', '',
          ...playerHead.map(() => ''),
          { content: pointsScoredLabel, colSpan: roundsCount },
          '', '', '',
        ],
        labelRow,
      ]
    : [labelRow];
  const body = teams.map((t) => {
    const pcells = maxP === 0
      ? [t.label]
      : maxP === 1
        ? [t.players[0] || '']
        : [t.players[0] || '', t.players[1] || ''];
    return [String(t.seed), '', ...pcells, ...rHead.map(() => ''), '', '', ''];
  });

  const playerColCount = playerHead.length;
  const columnStyles: any = {
    0: { cellWidth: 40, halign: 'center' },
    1: { cellWidth: 46, halign: 'center' },
  };
  for (let i = 0; i < playerColCount; i += 1) columnStyles[2 + i] = { cellWidth: 100, halign: 'left' };

  autoTable(doc, {
    startY,
    head,
    body,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, valign: 'middle', halign: 'center', minCellHeight: 20 },
    headStyles: { fillColor: [235, 235, 235], textColor: 20, fontStyle: 'bold', halign: 'center' },
    columnStyles,
    // bottom reserves the page-bottom Notes section (drawNotesSection).
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: 100 },
  });
  return lastY(doc);
}

// Court # | Round 1..N with the pre-generated matchups (e.g. "1 vs 2").
function drawRoundsTable(doc: jsPDF, schedule: RoundRobinSchedule, startY: number): number {
  const { rounds, courtsPerRound } = schedule;
  if (!rounds.length) return startY;
  const head = [['Court #', ...rounds.map((r) => `Round ${r.round}`)]];
  const body: string[][] = [];
  for (let c = 0; c < courtsPerRound; c += 1) {
    body.push([
      // Court # is left blank — the scorekeeper fills in the actual court.
      '',
      ...rounds.map((r) => {
        const m = r.matches[c];
        return m ? `${m.home} vs ${m.away}` : '';
      }),
    ]);
  }
  autoTable(doc, {
    startY: startY + 14,
    head,
    body,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, valign: 'middle', halign: 'center', minCellHeight: 20 },
    headStyles: { fillColor: [235, 235, 235], textColor: 20, fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 46, fontStyle: 'bold' } },
    // bottom reserves the page-bottom Notes section (drawNotesSection).
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: 100 },
  });
  return lastY(doc);
}

// My alternative: a round-robin cross-table (matrix) for recording head-to-heads.
function drawCrossTable(doc: jsPDF, teams: ScoresheetTeam[], startY: number): number {
  const seeds = teams.map((t) => String(t.seed));
  const head = [['#', 'Team', ...seeds, 'W', 'L', 'Pts', 'Place']];
  const body = teams.map((t) => {
    const cells = teams.map((o) => (o.seed === t.seed ? '' : ''));
    return [String(t.seed), teamCellLabel(t), ...cells, '', '', '', ''];
  });
  autoTable(doc, {
    startY,
    head,
    body,
    theme: 'grid',
    styles: {
      fontSize: 8, cellPadding: 4, valign: 'middle', halign: 'center', minCellHeight: 22,
    },
    headStyles: { fillColor: [235, 235, 235], textColor: 20, fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 130, halign: 'left' } },
    // bottom reserves the page-bottom Notes section (drawNotesSection).
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: 100 },
    // Shade each team's own column (the diagonal) so it reads as "no self-match".
    didParseCell: (hook: any) => {
      if (hook.section === 'body') {
        const rowSeed = hook.row.index + 1;
        const colSeed = hook.column.index - 1; // seed columns start at index 2 → seed = index-1
        if (colSeed === rowSeed) hook.cell.styles.fillColor = [210, 210, 210];
      }
    },
  });
  return lastY(doc);
}

// A blank single-elimination bracket for 2 (Final), 4 (SF → Final + 3rd) or
// 8 (QF → SF → Final + 3rd) seeds. Standard seeding so top seeds meet last.
function drawBracket(doc: jsPDF, teams: ScoresheetTeam[], startY: number, size = 8): void {
  const label = (seed: number) => {
    const t = teams[seed - 1];
    return t ? `${seed}. ${teamCellLabel(t)}` : `Seed ${seed}`;
  };
  const seeds = bracketSeeds(size); // first-round [high, low] pairs
  const boxW = 150;
  const boxH = 26;
  const colGap = 60;
  const x0 = PAGE_MARGIN;
  const yTop = startY + 10;
  const rowGap = 44;

  const drawBox = (x: number, y: number, text: string, filled = false) => {
    doc.setDrawColor(60);
    doc.setLineWidth(0.7);
    doc.rect(x, y, boxW, boxH);
    doc.setFontSize(8);
    doc.setFont('helvetica', filled ? 'bold' : 'normal');
    doc.text(text || '', x + 4, y + 16, { maxWidth: boxW - 8 });
  };
  const connector = (x1: number, y1: number, x2: number, y2: number) => {
    doc.setDrawColor(150);
    doc.setLineWidth(0.5);
    doc.line(x1, y1, x2, y2);
  };

  // Column headings end at "Championship"; size 2 → 1 col, 4 → 2, 8 → 3.
  const ROUND_NAMES = ['Quarterfinals', 'Semifinals', 'Championship'];
  const totalCols = Math.max(1, Math.round(Math.log2(size)));
  const headings = ROUND_NAMES.slice(ROUND_NAMES.length - totalCols);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  headings.forEach((h, i) => doc.text(h, x0 + i * (boxW + colGap), startY));

  // First column — seed pairs (filled).
  let centers: number[] = [];
  seeds.forEach((pair, i) => {
    const yA = yTop + i * (2 * boxH + rowGap);
    const yB = yA + boxH + 6;
    drawBox(x0, yA, label(pair[0]), true);
    drawBox(x0, yB, label(pair[1]), true);
    centers.push((yA + yB + boxH) / 2);
  });

  // Winner columns — halve until a single champion box remains.
  let col = 1;
  let finalCenter = centers[0];
  while (centers.length > 1) {
    const x = x0 + col * (boxW + colGap);
    const prevX = x0 + (col - 1) * (boxW + colGap);
    const next: number[] = [];
    for (let i = 0; i < centers.length / 2; i += 1) {
      const y = (centers[2 * i] + centers[2 * i + 1]) / 2 - boxH / 2;
      drawBox(x, y, '');
      connector(prevX + boxW, centers[2 * i], x, y + boxH / 2);
      connector(prevX + boxW, centers[2 * i + 1], x, y + boxH / 2);
      next.push(y + boxH / 2);
    }
    centers = next;
    finalCenter = centers[0];
    col += 1;
  }

  // 3rd-place box below the final — only when the bracket has semifinals (size ≥ 4).
  if (size >= 4) {
    const fX = x0 + (totalCols - 1) * (boxW + colGap);
    const fyTop = finalCenter - boxH / 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('3rd Place', fX, fyTop + boxH + 30);
    drawBox(fX, fyTop + boxH + 36, '');
  }
}

// The consolation half of an 8-team placement bracket: QF losers → Consolation
// Semifinals → 5th Place, with the CSF losers meeting in a 7th Place game.
// Geometry mirrors drawBracket's size-4 shape with placeholder-fed first slots.
function drawConsolationBracket(doc: jsPDF, startY: number): void {
  const boxW = 150;
  const boxH = 26;
  const colGap = 60;
  const x0 = PAGE_MARGIN;
  const yTop = startY + 10;
  const rowGap = 44;

  const drawBox = (x: number, y: number, text: string, filled = false) => {
    doc.setDrawColor(60);
    doc.setLineWidth(0.7);
    doc.rect(x, y, boxW, boxH);
    doc.setFontSize(8);
    doc.setFont('helvetica', filled ? 'bold' : 'normal');
    doc.text(text || '', x + 4, y + 16, { maxWidth: boxW - 8 });
  };
  const connector = (x1: number, y1: number, x2: number, y2: number) => {
    doc.setDrawColor(150);
    doc.setLineWidth(0.5);
    doc.line(x1, y1, x2, y2);
  };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Consolation Semifinals', x0, startY);
  doc.text('5th Place', x0 + boxW + colGap, startY);

  const feeders = [
    ['Loser of Quarterfinal 1', 'Loser of Quarterfinal 2'],
    ['Loser of Quarterfinal 3', 'Loser of Quarterfinal 4'],
  ];
  const centers: number[] = [];
  feeders.forEach((pair, i) => {
    const yA = yTop + i * (2 * boxH + rowGap);
    const yB = yA + boxH + 6;
    drawBox(x0, yA, pair[0], true);
    drawBox(x0, yB, pair[1], true);
    centers.push((yA + yB + boxH) / 2);
  });

  const x1 = x0 + boxW + colGap;
  const fifthY = (centers[0] + centers[1]) / 2 - boxH / 2;
  drawBox(x1, fifthY, '');
  connector(x0 + boxW, centers[0], x1, fifthY + boxH / 2);
  connector(x0 + boxW, centers[1], x1, fifthY + boxH / 2);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('7th Place (Consolation Semifinal losers)', x1, fifthY + boxH + 30);
  drawBox(x1, fifthY + boxH + 36, '');
}

// ── format builders ─────────────────────────────────────────────────────────

function buildRoundRobinStandard(doc: jsPDF, teams: ScoresheetTeam[], roundsPerMatchup: number, startY: number): void {
  const schedule = generateRoundRobinSchedule(teams.length, roundsPerMatchup);
  const y = drawRosterGrid(doc, teams, schedule.rounds.length, startY, {
    pointsScored: 'Points Scored (Circle Winner)',
    points: 'Total Points',
  });
  drawRoundsTable(doc, schedule, y);
}

function buildRoundRobinMatrix(doc: jsPDF, teams: ScoresheetTeam[], startY: number): void {
  drawCrossTable(doc, teams, startY);
}

function buildRoundRobinPlayoffs(doc: jsPDF, teams: ScoresheetTeam[], roundsPerMatchup: number, startY: number, playoffTeams: number): void {
  const schedule = generateRoundRobinSchedule(teams.length, roundsPerMatchup);
  const y = drawRosterGrid(doc, teams, schedule.rounds.length, startY);
  drawRoundsTable(doc, schedule, y);
  doc.addPage();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(`Playoffs: Top ${playoffTeams} Seeds`, PAGE_MARGIN, PAGE_MARGIN);
  // Reuse the bracket drawer with `playoffTeams` "Seed N" slots.
  const seedTeams: ScoresheetTeam[] = Array.from({ length: playoffTeams }, (_, i) => ({ seed: i + 1, label: `Seed ${i + 1}`, players: [] }));
  drawBracket(doc, seedTeams, PAGE_MARGIN + 16, playoffTeams);
}

function buildBracket(doc: jsPDF, teams: ScoresheetTeam[], startY: number, size: number): void {
  drawBracket(doc, teams, startY, size);
}

function buildPoolPlay(doc: jsPDF, teams: ScoresheetTeam[], pools: ScoresheetTeam[][], roundsPerMatchup: number, startY: number): void {
  pools.forEach((poolTeams, idx) => {
    if (idx > 0) doc.addPage();
    const headingY = idx === 0 ? startY : PAGE_MARGIN + 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(`Pool ${String.fromCharCode(65 + idx)}`, PAGE_MARGIN, headingY);
    // Re-seed within the pool for clean 1..k numbering + matchups.
    const reseeded = poolTeams.map((t, i) => ({ ...t, seed: i + 1 }));
    const schedule = generateRoundRobinSchedule(reseeded.length, roundsPerMatchup);
    const y = drawRosterGrid(doc, reseeded, schedule.rounds.length, headingY + 12);
    drawRoundsTable(doc, schedule, y);
  });
}

function buildPoolPlayPlayoffs(doc: jsPDF, teams: ScoresheetTeam[], pools: ScoresheetTeam[][], roundsPerMatchup: number, startY: number, playoffTeams: number): void {
  buildPoolPlay(doc, teams, pools, roundsPerMatchup, startY);
  doc.addPage();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Playoffs: Pool Finishers', PAGE_MARGIN, PAGE_MARGIN);
  // Prefer pool-finisher labels ("Pool A #1", …); pad/trim to the chosen bracket size.
  const poolSlots: ScoresheetTeam[] = [];
  pools.forEach((_, idx) => {
    const letter = String.fromCharCode(65 + idx);
    poolSlots.push({ seed: poolSlots.length + 1, label: `Pool ${letter} #1`, players: [] });
    poolSlots.push({ seed: poolSlots.length + 1, label: `Pool ${letter} #2`, players: [] });
  });
  const slots: ScoresheetTeam[] = Array.from({ length: playoffTeams }, (_, i) => (
    poolSlots[i] ?? { seed: i + 1, label: `Seed ${i + 1}`, players: [] }
  ));
  drawBracket(doc, slots, PAGE_MARGIN + 16, playoffTeams);
}

// ── dispatcher ──────────────────────────────────────────────────────────────

export function buildScoresheetDoc(format: string, variantId: string, data: any): jsPDF {
  const doc = newDoc();
  const teams = extractTeams(data.teams);
  const roundsPerMatchup = data?.division?.config?.roundsPerMatchup || 1;
  const playoffTeams = data?.division?.config?.playoffTeams || defaultPlayoffTeams(format);

  const subtitle = data.leagueName ? String(data.leagueName) : undefined;
  // The round-robin standard sheet drops the Date/Time/Location write-in row;
  // every other format/variant keeps it.
  const rrStandard = format === 'round-robin' && variantId !== 'matrix';
  const startY = drawHeaderBlock(doc, {
    title: data?.division?.name || 'Division',
    subtitle,
    numTeams: teams.length,
    showDateLine: !rrStandard,
  }) + 6;

  switch (format) {
    case 'round-robin':
      if (variantId === 'matrix') buildRoundRobinMatrix(doc, teams, startY);
      else buildRoundRobinStandard(doc, teams, roundsPerMatchup, startY);
      break;
    case 'round-robin-playoffs':
      buildRoundRobinPlayoffs(doc, teams, roundsPerMatchup, startY, playoffTeams);
      break;
    case 'bracket':
      buildBracket(doc, teams, startY, playoffTeams);
      break;
    case 'pool-play':
    case 'pool-play-playoffs': {
      const configPools = data?.division?.config?.pools;
      let pools: ScoresheetTeam[][];
      if (Array.isArray(configPools) && configPools.length) {
        pools = configPools.map((p: any) => (p.teamIds || [])
          .map((id: string) => teams.find((t, i) => String((data.teams[i])?._id) === String(id)))
          .filter(Boolean));
        // Fallback if mapping produced empties.
        if (!pools.some((p) => p.length)) pools = splitIntoPools(teams, Math.max(2, Math.ceil(teams.length / 4)));
      } else {
        pools = splitIntoPools(teams, Math.max(2, Math.ceil(teams.length / 4)));
      }
      if (format === 'pool-play') buildPoolPlay(doc, teams, pools, roundsPerMatchup, startY);
      else buildPoolPlayPlayoffs(doc, teams, pools, roundsPerMatchup, startY, playoffTeams);
      break;
    }
    case 'playoffs':
      buildBracket(doc, teams, startY, playoffTeams);
      break;
    case 'playoffs-consolation': {
      const size = normalizeBracketSize(playoffTeams, true);
      buildBracket(doc, teams, startY, size);
      // Size 4 already places everyone (Final + 3rd place); only the 8-team
      // bracket needs the extra consolation page.
      if (size === 8) {
        doc.addPage();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text('Consolation Bracket: 5th–8th Place', PAGE_MARGIN, PAGE_MARGIN);
        drawConsolationBracket(doc, PAGE_MARGIN + 16);
      }
      break;
    }
    default:
      buildRoundRobinStandard(doc, teams, roundsPerMatchup, startY);
  }

  // Every page gets a bottom "Notes:" write-in section.
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    drawNotesSection(doc);
  }
  return doc;
}
